import {
  ConditionOperatorKey,
  ConditionOperatorKeyToText,
} from 'common/src/modules/conditions/conditions.types';
import {
  DTConditionNode,
  ConditionNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {toAiConditionCategory} from '../aiModels/cardDetailsAI.enums';
import {
  getBaseHeader,
  getCategoryUINamePart,
  getDestinationPhrase,
  getOutputIdentifier,
} from './utils';

const FALLBACK_WHEN_CLAUSE = 'when none of the conditions above are met';
const DEFAULT_EMPTY = '[]';

function formatQuotedList(values: string[]): string {
  if (!values.length) {
    return DEFAULT_EMPTY;
  }

  return `[${values.map((value) => `'${value}'`).join(', ')}]`;
}

function formatEqualPhrase(values: string[]): string {
  if (!values.length) {
    return '';
  }

  if (values.length === 1) {
    return `'${values[0]}'`;
  }

  return `any of ${formatQuotedList(values)}`;
}

const TEXT_TYPES = [MacroItemTypeValue.Text, MacroItemTypeValue.TextList];
const DATE_TYPES = [
  MacroItemTypeValue.Date,
  MacroItemTypeValue.DateList,
  MacroItemTypeValue.DateRange,
];

function isTextType(condKeyType: MacroItemTypeValue | undefined): boolean {
  return condKeyType !== undefined && TEXT_TYPES.includes(condKeyType);
}

function isNumericType(condKeyType: MacroItemTypeValue | undefined): boolean {
  return condKeyType === MacroItemTypeValue.Numeric;
}

function isDateType(condKeyType: MacroItemTypeValue | undefined): boolean {
  return condKeyType !== undefined && DATE_TYPES.includes(condKeyType);
}

function isBooleanType(condKeyType: MacroItemTypeValue | undefined): boolean {
  return condKeyType === MacroItemTypeValue.Condition;
}

function buildConditionSentence(
  operator: ConditionOperatorKey,
  output: ConditionNodeOutputData,
  condKeyType?: MacroItemTypeValue,
): string {
  const textValues = (output.textValues ?? []).map(String);
  const numericValues = (output.numericValues ?? []).map(String);
  const dateValues = (output.dateValues ?? []).map((d) => d.toISO());
  const textListQuoted = formatQuotedList(textValues);
  const datesRange = output.datesRange;
  const numbersRange = output.numbersRange;

  const equalValues = isTextType(condKeyType)
    ? textValues
    : isNumericType(condKeyType)
      ? numericValues
      : isDateType(condKeyType)
        ? dateValues
        : [];

  const firstDate = output.dateValues?.[0];
  const firstNumeric = output.numericValues?.[0];

  switch (operator) {
    case ConditionOperatorKey.Equal:
      if (isBooleanType(condKeyType)) {
        return `when signal is ${output.booleanValue}`;
      }

      if (equalValues.length === 0) {
        return '';
      }

      {
        const phrase = formatEqualPhrase(equalValues);

        return `when signal equals exactly ${phrase}`;
      }

    case ConditionOperatorKey.NotEqual:
      if (equalValues.length === 0) {
        return '';
      }

      {
        const phrase = formatEqualPhrase(equalValues);

        return `when signal not equals ${phrase}`;
      }

    case ConditionOperatorKey.Contains:
      if (textValues.length === 0) {
        return '';
      }

      if (condKeyType === MacroItemTypeValue.Text) {
        if (textValues.length === 1) {
          return `when signal contains substring '${textValues[0]}'`;
        }

        return `when signal contains any of the substrings ${textListQuoted}`;
      }

      if (condKeyType === MacroItemTypeValue.TextList) {
        if (textValues.length === 1) {
          return `when signal contains value '${textValues[0]}'`;
        }

        return `when signal contains any of exact values ${textListQuoted}`;
      }

      throw new Error('Contains is supported only for Text and TextList signal types');

    case ConditionOperatorKey.NotContains:
      if (textValues.length === 0) {
        return '';
      }

      if (condKeyType === MacroItemTypeValue.Text) {
        if (textValues.length === 1) {
          return `when signal not contains substring '${textValues[0]}'`;
        }

        return `when signal not contains all of substrings ${textListQuoted}`;
      }

      if (condKeyType === MacroItemTypeValue.TextList) {
        if (textValues.length === 1) {
          return `when signal not contains value '${textValues[0]}'`;
        }

        return `when signal not contains values ${textListQuoted}`;
      }

      throw new Error('NotContains is supported only for Text and TextList signal types');

    case ConditionOperatorKey.Between:
      if (isDateType(condKeyType) && datesRange) {
        return `when signal is between ${datesRange.from.toISO()} (including) and ${datesRange.to.toISO()} (including)`;
      }

      if (isNumericType(condKeyType) && numbersRange != null) {
        return `when signal is between ${numbersRange.from} (including) and ${numbersRange.to} (including)`;
      }

      return '';

    case ConditionOperatorKey.LessThan:
      if (isDateType(condKeyType) && firstDate) {
        return `when signal is less than ${firstDate.toISO()}`;
      }

      if (isNumericType(condKeyType) && firstNumeric !== undefined) {
        return `when signal is less than ${firstNumeric}`;
      }

      return '';

    case ConditionOperatorKey.LessThanOrEqual:
      if (isDateType(condKeyType) && firstDate) {
        return `when signal is less than or equal to ${firstDate.toISO()}`;
      }

      if (isNumericType(condKeyType) && firstNumeric !== undefined) {
        return `when signal is less than or equal to ${firstNumeric}`;
      }

      return '';

    case ConditionOperatorKey.GreaterThan:
      if (isDateType(condKeyType) && firstDate) {
        return `when signal is greater than ${firstDate.toISO()}`;
      }

      if (isNumericType(condKeyType) && firstNumeric !== undefined) {
        return `when signal is greater than ${firstNumeric}`;
      }

      return '';

    case ConditionOperatorKey.GreaterThanOrEqual:
      if (isDateType(condKeyType) && firstDate) {
        return `when signal is greater than or equal to ${firstDate.toISO()}`;
      }

      if (isNumericType(condKeyType) && firstNumeric !== undefined) {
        return `when signal is greater than or equal to ${firstNumeric}`;
      }

      return '';

    case ConditionOperatorKey.Exists:
      if (isBooleanType(condKeyType)) {
        return `when signal is ${output.booleanValue}`;
      }

      return `when signal value exists`;

    default:
      throw new Error(`Unknown condition operator: ${operator}`);
  }
}

function formatConditionOutputLine(
  card: DTConditionNode,
  output: ConditionNodeOutputData,
  includeValues: boolean,
): string {
  const outputId = getOutputIdentifier(output);
  const dest = getDestinationPhrase(output.nodeId);
  const isUnconnected = !output.nodeId;
  const isFallback = output.type === OutputType.Fallback;

  if (!includeValues) {
    if (isFallback) {
      return `Output ${outputId} (fallback) ${dest}.`;
    }

    return `Output ${outputId} ${dest}.`;
  }

  if (isFallback) {
    const fallbackPart = isUnconnected
      ? `${dest} ${FALLBACK_WHEN_CLAUSE}`
      : `${dest} ${FALLBACK_WHEN_CLAUSE}`;

    return `Output ${outputId} (fallback) ${fallbackPart}.`;
  }

  const whenSentence = buildConditionSentence(card.condOperator, output, card.condKeyType);

  if (whenSentence === '') {
    return `Output ${outputId} ${dest}.`;
  }

  const conditionPart = `${dest} ${whenSentence}`;

  return `Output ${outputId} ${conditionPart}.`;
}

function formatSignalSection(card: DTConditionNode): string[] {
  const signalName = card.signalUIName ?? card.condKey ?? '';
  const categoryHierarchy = (card.categories ?? [])
    .map(toAiConditionCategory)
    .filter((c): c is string => c != null)
    .join(' > ');
  const operator =
    card.condOperatorUIName ??
    (card.condOperator != null ? ConditionOperatorKeyToText[card.condOperator] : '');

  return [
    `Signal Name: ${signalName}`,
    `Signal Key: ${card.condKey}`,
    `Signal Category Hierarchy: ${categoryHierarchy || ''}`,
    `Operator: ${operator}`,
  ];
}

export function conditionCardToText(card: DTConditionNode, mode: 'short' | 'full'): string {
  const header = getBaseHeader(card);
  const signalName = card.signalUIName ?? card.condKey ?? '';
  const purpose =
    mode === 'short'
      ? `Purpose: Choose output by ${getCategoryUINamePart(card.categories[0])}${signalName}`
      : null;
  const outputLines = card.outputs.map((output) =>
    formatConditionOutputLine(card, output, mode === 'full'),
  );

  const parts = [header, purpose];

  if (mode === 'full') {
    parts.push(...formatSignalSection(card));
  }

  parts.push(...outputLines);

  return parts.filter(Boolean).join('\n');
}
